/**
 * Way2Buy Theme – globale Interaktionen (Event Delegation)
 */
(function (W2B) {
  'use strict';

  W2B.getShopLocale = function getShopLocale() {
    return document.body?.dataset.shopLocale || document.documentElement.lang || 'de-DE';
  };

  W2B.getShopCurrency = function getShopCurrency() {
    return document.body?.dataset.shopCurrency || window.Shopify?.currency?.active || 'EUR';
  };

  W2B.formatMoney = function formatMoney(amount) {
    const value = Number(amount);
    return new Intl.NumberFormat(W2B.getShopLocale(), {
      style: 'currency',
      currency: W2B.getShopCurrency(),
    }).format(Number.isFinite(value) ? value : 0);
  };

  let mobileNavBound = false;
  let headerSearchBound = false;
  let summaryBound = false;
  let variantsBound = false;
  let documentEventsBound = false;

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getFocusable(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );
  }

  // Hält Tab-Fokus innerhalb von container; gibt einen Cleanup-Handler zurück.
  function createFocusTrap(container) {
    const handler = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = getFocusable(container);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  }

  let mobileNavLastFocus = null;
  let mobileNavReleaseTrap = null;

  function closeMobileNav() {
    const nav = document.querySelector('[data-mobile-nav]');
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const backdrop = document.querySelector('[data-mobile-nav-backdrop]');
    if (!nav || !toggle) return;
    const wasOpen = nav.classList.contains('is-open');
    nav.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', document.body.dataset.i18nMenuOpen || 'Open menu');
    document.body.classList.remove('mobile-nav-open');
    if (backdrop) backdrop.hidden = true;
    if (mobileNavReleaseTrap) {
      mobileNavReleaseTrap();
      mobileNavReleaseTrap = null;
    }
    if (wasOpen && mobileNavLastFocus && typeof mobileNavLastFocus.focus === 'function') {
      mobileNavLastFocus.focus();
    }
    mobileNavLastFocus = null;
  }

  function initMobileNav() {
    if (mobileNavBound) return;
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const nav = document.querySelector('[data-mobile-nav]');
    const backdrop = document.querySelector('[data-mobile-nav-backdrop]');
    const menuOpenLabel = document.body.dataset.i18nMenuOpen || 'Open menu';
    const menuCloseLabel = document.body.dataset.i18nMenuClose || 'Close menu';
    if (!toggle || !nav) return;
    mobileNavBound = true;

    toggle.addEventListener('click', () => {
      const isOpen = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(isOpen));
      toggle.setAttribute('aria-label', isOpen ? menuCloseLabel : menuOpenLabel);
      document.body.classList.toggle('mobile-nav-open', isOpen);
      if (backdrop) backdrop.hidden = !isOpen;

      if (isOpen) {
        mobileNavLastFocus = toggle;
        mobileNavReleaseTrap = createFocusTrap(nav);
        const focusable = getFocusable(nav);
        if (focusable.length) focusable[0].focus();
      } else {
        if (mobileNavReleaseTrap) {
          mobileNavReleaseTrap();
          mobileNavReleaseTrap = null;
        }
      }
    });

    backdrop?.addEventListener('click', closeMobileNav);

    nav.addEventListener('click', (event) => {
      if (event.target.closest('a')) closeMobileNav();
    });
  }


  function closeHeaderSearch(form, restoreFocus = false) {
    if (!form) return;
    const input = form.querySelector('[data-header-search-input]');
    const button = form.querySelector('[data-header-search-button]');
    form.classList.remove('is-open');
    button?.setAttribute('aria-expanded', 'false');
    button?.setAttribute('aria-label', document.body.dataset.i18nSearchOpen || 'Open search');
    input?.setAttribute('tabindex', '-1');
    if (restoreFocus && button && typeof button.focus === 'function') button.focus();
  }

  function openHeaderSearch(form) {
    if (!form) return;
    const input = form.querySelector('[data-header-search-input]');
    const button = form.querySelector('[data-header-search-button]');
    form.classList.add('is-open');
    button?.setAttribute('aria-expanded', 'true');
    button?.setAttribute('aria-label', document.body.dataset.i18nSearchSubmit || 'Submit search');
    input?.removeAttribute('tabindex');
    window.requestAnimationFrame(() => input?.focus());
  }

  function initHeaderSearch() {
    if (headerSearchBound) return;
    const forms = document.querySelectorAll('[data-header-search]');
    if (!forms.length) return;
    headerSearchBound = true;

    forms.forEach((form) => {
      const input = form.querySelector('[data-header-search-input]');
      const button = form.querySelector('[data-header-search-button]');
      const closeBtn = form.querySelector('[data-header-search-close]');
      if (!input || !button) return;

      form.addEventListener('submit', (event) => {
        const isOpen = form.classList.contains('is-open');
        const value = input.value.trim();

        if (!isOpen) {
          event.preventDefault();
          openHeaderSearch(form);
          return;
        }

        if (!value) {
          event.preventDefault();
          input.focus();
        }
      });

      button.addEventListener('click', (event) => {
        if (!form.classList.contains('is-open')) {
          event.preventDefault();
          openHeaderSearch(form);
        }
      });

      closeBtn?.addEventListener('click', () => {
        input.value = '';
        closeHeaderSearch(form, true);
      });

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          input.value = '';
          closeHeaderSearch(form, true);
        }
      });
    });

    document.addEventListener('click', (event) => {
      forms.forEach((form) => {
        if (form.contains(event.target)) return;
        const input = form.querySelector('[data-header-search-input]');
        if (!input?.value.trim()) closeHeaderSearch(form, false);
      });
    });
  }

  function initDocumentEvents() {
    if (documentEventsBound) return;
    documentEventsBound = true;

    document.addEventListener('keydown', (event) => {
      const lb = document.getElementById('w2b-lightbox');
      if (lb && !lb.hidden) {
        const thumbs = document.querySelectorAll('[data-pdp-gallery-thumb]');
        const activeGallery = document.querySelector('[data-pdp-gallery]');
        const idx = parseInt(activeGallery?.dataset.galleryIndex, 10) || 0;
        if (event.key === 'Escape') {
          closeProductLightbox();
          return;
        }
        if (event.key === 'ArrowRight' && thumbs.length) {
          event.preventDefault();
          switchGalleryThumb(thumbs[(idx + 1) % thumbs.length]);
          return;
        }
        if (event.key === 'ArrowLeft' && thumbs.length) {
          event.preventDefault();
          switchGalleryThumb(thumbs[(idx - 1 + thumbs.length) % thumbs.length]);
          return;
        }
        if (event.key === 'Tab') {
          event.preventDefault();
          document.getElementById('w2b-lightbox-close')?.focus();
          return;
        }
      }

      if (event.key !== 'Escape') return;
      const icecatLb = document.getElementById('icecat-lightbox');
      if (icecatLb && icecatLb.classList.contains('is-open')) {
        icecatLb.classList.remove('is-open');
        if (window.W2B?.scrollLock) window.W2B.scrollLock.unlock();
        else document.body.style.overflow = '';
        return;
      }
      closeMobileNav();
    });

    document.addEventListener('click', (event) => {
      const mainBtn = event.target.closest('[data-pdp-gallery-main]');
      if (mainBtn && mainBtn.tagName === 'BUTTON') {
        event.preventDefault();
        openProductLightbox(mainBtn);
        return;
      }

      const thumbBtn = event.target.closest('[data-pdp-gallery-thumb]');
      if (thumbBtn) {
        event.preventDefault();
        switchGalleryThumb(thumbBtn);
        return;
      }

      if (event.target.id === 'w2b-lightbox') {
        closeProductLightbox();
        return;
      }

      if (event.target.closest('#w2b-lightbox-close')) {
        closeProductLightbox();
        return;
      }

      const icecatImg = event.target.closest('.icecat-gallery-grid img[data-full]');
      if (icecatImg && typeof window._icecatLb === 'function') {
        window._icecatLb(icecatImg.getAttribute('data-full') || icecatImg.src);
        return;
      }

      const faqBtn = event.target.closest('[data-faq-toggle]');
      if (faqBtn) {
        const item = faqBtn.closest('.faq-item');
        const answer = item?.querySelector('.faq-item__answer');
        if (!item || !answer) return;
        const isOpen = item.classList.toggle('is-open');
        faqBtn.setAttribute('aria-expanded', String(isOpen));
        answer.hidden = !isOpen;
        return;
      }

      const tabBtn = event.target.closest('.product-tab-btn, .pdp-tabs__btn');
      if (tabBtn) {
        const tabs = tabBtn.closest('.product-tabs, [data-pdp-tabs]');
        if (!tabs) return;
        const tabId = tabBtn.getAttribute('data-tab');
        tabs.querySelectorAll('.product-tab-btn, .pdp-tabs__btn').forEach((btn) => {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-selected', 'false');
        });
        tabs.querySelectorAll('.product-tab-panel, .pdp-tabs__panel').forEach((panel) => {
          panel.classList.remove('is-active');
          panel.hidden = true;
        });
        tabBtn.classList.add('is-active');
        tabBtn.setAttribute('aria-selected', 'true');
        const panel = tabs.querySelector(`[data-tab-panel="${tabId}"]`);
        if (panel) {
          panel.classList.add('is-active');
          panel.hidden = false;
        }
        return;
      }

      const minus = event.target.closest('[data-qty-minus]');
      const plus = event.target.closest('[data-qty-plus]');
      const control = minus || plus;
      if (control) {
        const input = control.parentElement?.querySelector('input[type="number"]');
        if (!input) return;
        const min = parseInt(input.min, 10) || 1;
        const max = input.hasAttribute('max') ? parseInt(input.max, 10) : null;
        const current = parseInt(input.value, 10) || min;
        const next = minus ? Math.max(min, current - 1) : current + 1;
        input.value = String(Number.isFinite(max) ? Math.min(max, next) : next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
  }

  function initFaq() {
    document.querySelectorAll('.faq-item.is-open .faq-item__answer').forEach((answer) => {
      answer.hidden = false;
    });
  }

  function initQuantity() {
    /* Delegation in initDocumentEvents */
  }

  function initProductTabs() {
    /* Delegation in initDocumentEvents */
  }

  function openProductLightbox(mainBtn) {
    const lb = document.getElementById('w2b-lightbox');
    const lbImg = document.getElementById('w2b-lightbox-img');
    const img = mainBtn?.querySelector('img');
    if (!lb || !lbImg || !img) return;

    window._w2bLbLastFocus = mainBtn;
    lbImg.src = mainBtn.getAttribute('data-full-src') || img.currentSrc || img.src;
    lbImg.alt = img.alt || '';
    lb.hidden = false;
    lb.classList.add('is-open');
    if (W2B.scrollLock) W2B.scrollLock.lock();
    else document.body.style.overflow = 'hidden';
    document.getElementById('w2b-lightbox-close')?.focus();
  }

  function closeProductLightbox() {
    const lb = document.getElementById('w2b-lightbox');
    if (!lb) return;
    lb.hidden = true;
    lb.classList.remove('is-open');
    if (W2B.scrollLock) W2B.scrollLock.unlock();
    else document.body.style.overflow = '';
    const last = window._w2bLbLastFocus;
    if (last?.focus) last.focus();
    window._w2bLbLastFocus = null;
  }

  function switchGalleryThumb(thumbBtn) {
    const gallery = thumbBtn.closest('[data-pdp-gallery]');
    const thumbs = gallery ? Array.from(gallery.querySelectorAll('[data-pdp-gallery-thumb]')) : [];
    const idx = thumbs.indexOf(thumbBtn);
    if (idx < 0) return;

    const mainBtn = gallery.querySelector('[data-pdp-gallery-main]');
    const mainImg = mainBtn?.querySelector('img');
    const counter = gallery.querySelector('[data-pdp-gallery-counter]');
    const src = thumbBtn.getAttribute('data-image-src');
    const alt = thumbBtn.getAttribute('data-image-alt') || '';

    if (mainImg && src) {
      mainImg.removeAttribute('srcset');
      mainImg.removeAttribute('sizes');
      mainImg.src = src;
      mainImg.alt = alt;
    }
    if (mainBtn && src) mainBtn.setAttribute('data-full-src', src);

    thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === idx);
      if (i === idx) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    if (counter) counter.textContent = `${idx + 1} von ${thumbs.length}`;
    gallery.dataset.galleryIndex = String(idx);
    window._w2bGalleryIdx = idx;

    const lb = document.getElementById('w2b-lightbox');
    const lbImg = document.getElementById('w2b-lightbox-img');
    if (lb && lbImg && !lb.hidden && src) {
      lbImg.src = src;
      lbImg.alt = alt;
    }
  }

  window.w2bOpenLb = openProductLightbox;
  window.w2bCloseLb = closeProductLightbox;
  window.w2bSwitchThumb = switchGalleryThumb;

  function isProductLightboxOpen() {
    const lb = document.getElementById('w2b-lightbox');
    return lb && !lb.hidden;
  }

  function initProductSummary() {
    if (summaryBound) return;
    const toggles = document.querySelectorAll('[data-pdp-summary-toggle]');
    if (!toggles.length) return;
    summaryBound = true;

    toggles.forEach((btn) => {
      btn.addEventListener('click', () => {
        const wrap = btn.closest('[data-pdp-summary]');
        if (!wrap) return;

        const full = wrap.querySelector('[data-pdp-summary-full]');
        const expanded = wrap.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', String(expanded));
        if (full) full.hidden = !expanded;
        btn.textContent = expanded
          ? (document.body.dataset.i18nShowLess || 'Show less')
          : (document.body.dataset.i18nShowMore || 'Show more');
      });
    });
  }

  function initConfiguratorHashScroll() {
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;
    let target;
    try {
      target = document.querySelector(hash);
    } catch {
      return;
    }
    if (!target) return;
    window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function formatMoney(cents) {
    const amount = cents / 100;
    return W2B.formatMoney(amount);
  }

  function initProductVariants() {
    const section = document.querySelector('[data-product-section]');
    const jsonEl = section?.querySelector('[data-product-json]');
    if (!section || !jsonEl || variantsBound) return;
    variantsBound = true;

    let product;
    try {
      product = JSON.parse(jsonEl.textContent);
    } catch {
      return;
    }

    const variantInput = section.querySelector('[data-product-variant-id]');
    const priceEl = section.querySelector('[data-b2b-main-price]') || section.querySelector('[data-product-price]');
    const skuEl = section.querySelector('[data-product-sku]');
    const barcodeEl = section.querySelector('[data-product-barcode]');
    const availabilityText = section.querySelector('[data-product-availability-text]');
    const availabilityDot = section.querySelector('.b2b-config__availability-dot') || section.querySelector('.product-buybox__availability-dot');
    const submitBtns = section.querySelectorAll('[data-b2b-submit], [data-b2b-sticky-submit], [data-product-submit]');
    const configurator = section.querySelector('[data-b2b-configurator]');
    const optionSelects = section.querySelectorAll('[data-product-option]');
    const priceOnRequest = section.dataset.priceOnRequest || 'Auf Anfrage';
    const availableText = section.dataset.availabilityAvailable || 'Verfügbar';
    const unavailableText = section.dataset.availabilityUnavailable || 'Nicht verfügbar';

    const findVariant = () => {
      if (optionSelects.length === 0) return product.variants[0];
      const options = Array.from(optionSelects).map((select) => select.value);
      return product.variants.find((variant) =>
        variant.options.every((opt, i) => opt === options[i])
      );
    };

    const updateVariant = () => {
      const variant = findVariant();
      if (!variant) return;

      if (variantInput) variantInput.value = variant.id;

      const hidePrices = section.dataset.hidePrices === 'true';
      const priceOnRequestActive = hidePrices || !variant.price || variant.price <= 0;
      if (priceEl) {
        priceEl.textContent = priceOnRequestActive ? priceOnRequest : formatMoney(variant.price);
      }
      if (configurator) {
        configurator.dataset.basePrice = String(variant.price || 0);
      }
      if (skuEl && variant.sku) skuEl.textContent = variant.sku;
      if (barcodeEl && variant.barcode) barcodeEl.textContent = variant.barcode;

      if (availabilityText) {
        availabilityText.textContent = variant.available ? availableText : unavailableText;
      }
      if (availabilityDot) {
        availabilityDot.classList.toggle('b2b-config__availability-dot--muted', !variant.available);
        availabilityDot.classList.toggle('product-buybox__availability-dot--muted', !variant.available);
      }

      submitBtns.forEach((btn) => {
        btn.disabled = !variant.available;
      });

      if (typeof W2B.updateB2BConfigurator === 'function') {
        W2B.updateB2BConfigurator();
      } else if (typeof W2B.updateProcurementRate === 'function') {
        W2B.updateProcurementRate();
      }

      if (variant.featured_image) {
        const gallery = section.querySelector('[data-pdp-gallery]');
        // Passendes Thumbnail suchen (Vergleich über Dateinamen, da
        // variant.featured_image.id eine Bild-ID und keine Media-ID ist)
        const fileOf = (url) => (url || '').split('?')[0].split('/').pop();
        const variantFile = fileOf(variant.featured_image.src);
        const thumbs = gallery ? Array.from(gallery.querySelectorAll('[data-pdp-gallery-thumb]')) : [];
        const matchingThumb = thumbs.find(
          (thumb) => variantFile !== '' && fileOf(thumb.dataset.imageSrc) === variantFile
        );

        if (matchingThumb && typeof window.w2bSwitchThumb === 'function') {
          // Hält Hauptbild, Thumbnail-Status, Zähler und Lightbox synchron
          window.w2bSwitchThumb(matchingThumb);
        } else {
          const mainBtn = gallery?.querySelector('[data-pdp-gallery-main]');
          const mainImg = mainBtn?.querySelector('img');
          if (mainImg) {
            // srcset entfernen, sonst ignoriert der Browser das neue src
            mainImg.removeAttribute('srcset');
            mainImg.removeAttribute('sizes');
            mainImg.src = variant.featured_image.src;
            mainImg.alt = variant.featured_image.alt || product.title;
          }
          if (mainBtn) mainBtn.setAttribute('data-full-src', variant.featured_image.src);
        }
      }
    };

    optionSelects.forEach((select) => select.addEventListener('change', updateVariant));
    updateVariant();
  }

  W2B.init = function init() {
    initDocumentEvents();
    initMobileNav();
    initHeaderSearch();
    initFaq();
    initQuantity();
    initProductTabs();
    initProductSummary();
    initProductVariants();
    initConfiguratorHashScroll();

    const gallery = document.querySelector('[data-pdp-gallery]');
    if (gallery) {
      window._w2bGalleryIdx = parseInt(gallery.dataset.galleryIndex, 10) || 0;
    }

    if (typeof W2B.initB2BConfigurator === 'function') {
      W2B.initB2BConfigurator();
    } else if (typeof W2B.initProcurement === 'function') {
      W2B.initProcurement();
    }

    if (typeof W2B.initCollectionFilters === 'function') {
      W2B.initCollectionFilters();
    }

    if (typeof W2B.initCartB2B === 'function') {
      W2B.initCartB2B();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', W2B.init);
  } else {
    W2B.init();
  }

  document.addEventListener('shopify:section:load', () => {
    mobileNavBound = false;
    headerSearchBound = false;
    summaryBound = false;
    variantsBound = false;
    document.querySelector('[data-b2b-configurator]')?.removeAttribute('data-b2b-bound');
    document.querySelector('[data-b2b-configurator]')?.removeAttribute('data-b2b-initialized');
    document.querySelector('#product-form')?.removeAttribute('data-b2b-form-bound');
    document.querySelector('[data-b2b-sticky]')?.removeAttribute('data-sticky-bound');
    document.querySelector('[data-collection-sidebar]')?.removeAttribute('data-filters-bound');
    document.querySelector('.cart-b2b-form')?.removeAttribute('data-qty-bound');
    document.querySelector('.cart-b2b-checkout-form')?.removeAttribute('data-checkout-bound');
    W2B.init();
  });
})(window.W2B = window.W2B || {});
